import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { DeletedUserBadge } from "@/components/DeletedUserBadge";
import { toast } from "@/components/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Route as RouteIcon, Trash2, Plus, CheckCircle2, Circle, Gauge, Fuel, Sparkles, ArrowUp, ArrowDown, X, Info, CalendarDays } from "lucide-react";
import { SiteSelect } from "@/components/SiteSelect";

const todayStr = () => new Date().toISOString().slice(0, 10);
const monthAgoStr = () => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); };
const planKey = (p) => `${p.client_id}::${p.site_id || ""}`;

function mondayOf(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const dow = d.getDay();
  d.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow));
  return d.toISOString().slice(0, 10);
}
function addDays(dateStr, n) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}
function weeksOfMonth(mondayStr) {
  const base = new Date(mondayStr + "T00:00:00");
  const firstDay = new Date(base.getFullYear(), base.getMonth(), 1);
  const lastDay = new Date(base.getFullYear(), base.getMonth() + 1, 0);
  const mondays = new Set();
  for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
    mondays.add(mondayOf(d.toISOString().slice(0, 10)));
  }
  return Array.from(mondays).sort();
}
function weekLabel(mondayStr) {
  return `Semana del ${mondayStr.slice(8, 10)}/${mondayStr.slice(5, 7)} al ${addDays(mondayStr, 6).slice(8, 10)}/${addDays(mondayStr, 6).slice(5, 7)}`;
}

export default function SupervisorRoutes() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const backLink = isAdmin ? "/admin" : "/supervisor";
  const [supervisors, setSupervisors] = useState([]);
  const [clients, setClients] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [kmFuel, setKmFuel] = useState(null);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({ supervisor_id: isAdmin ? "" : user.id, date: todayStr(), planned: [], vehicle_plate: "", notes: "" });
  const [pickClient, setPickClient] = useState("");
  const [pickSite, setPickSite] = useState("");
  const [filters, setFilters] = useState({ supervisor_id: isAdmin ? "" : user.id, plate: "", date_from: monthAgoStr(), date_to: todayStr() });

  useEffect(() => { (async () => {
    try {
      const promises = [api.get("/clients")];
      if (isAdmin) promises.push(api.get("/users/roster?role=supervisor"));
      const [c, s] = await Promise.all(promises);
      setClients(c.data); if (s) setSupervisors(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
  })(); /* eslint-disable-next-line */ }, []);

  const refresh = async () => {
    try {
      const rp = new URLSearchParams();
      if (filters.supervisor_id) rp.set("supervisor_id", filters.supervisor_id);
      if (filters.date_from) rp.set("date_from", filters.date_from);
      if (filters.date_to) rp.set("date_to", filters.date_to);
      const kp = new URLSearchParams(rp); if (filters.plate) kp.set("plate", filters.plate);
      const [r, k] = await Promise.all([api.get(`/supervisor-routes?${rp.toString()}`), api.get(`/supervisor-routes/km-fuel?${kp.toString()}`)]);
      setRoutes(r.data); setKmFuel(k.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filters.supervisor_id, filters.plate, filters.date_from, filters.date_to]);

  const addClient = () => {
    if (!pickClient) return;
    const key = `${pickClient}::${pickSite || ""}`;
    if (form.planned.some((p) => planKey(p) === key)) return;
    const cli = clients.find((c) => c.id === pickClient);
    setForm((f) => ({ ...f, planned: [...f.planned, { client_id: pickClient, client_name: cli?.name || "—", site_id: pickSite || null, site_name: null }] }));
    setPickClient(""); setPickSite("");
  };
  const removeClient = (key) => setForm((f) => ({ ...f, planned: f.planned.filter((p) => planKey(p) !== key) }));
  const moveClient = (idx, dir) => setForm((f) => {
    const arr = [...f.planned]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...f, planned: arr };
  });

  const suggestFromSchedule = async () => {
    if (!form.supervisor_id || !form.date) return toast.error("Elegí supervisor y fecha primero");
    try {
      const { data } = await api.get(`/supervisor-routes/schedule-hint?supervisor_id=${form.supervisor_id}&date=${form.date}`);
      const items = data.suggestions || [];
      if (items.length === 0) return toast.info(data.message || "Sin objetivos para sugerir");
      setForm((f) => {
        const existing = new Set(f.planned.map(planKey));
        const added = items.filter((d) => !existing.has(`${d.client_id}::${d.site_id || ""}`))
          .map((d) => ({ client_id: d.client_id, client_name: d.client_name, site_id: d.site_id || null, site_name: d.site_name || null }));
        return { ...f, planned: [...f.planned, ...added] };
      });
      toast.success(`${items.length} objetivo(s)/sede(s) sugerido(s)`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async (e) => {
    e.preventDefault(); if (busy) return;
    if (!form.supervisor_id) return toast.error("Elegí el supervisor");
    if (form.planned.length === 0) return toast.error("Agregá al menos un objetivo al plan del día");
    setBusy(true);
    try {
      await api.post("/supervisor-routes", {
        supervisor_id: form.supervisor_id, date: form.date,
        planned_clients: form.planned.map((p, i) => ({ client_id: p.client_id, site_id: p.site_id || null, order: i })),
        vehicle_plate: form.vehicle_plate || null, notes: form.notes || null,
      });
      toast.success("Recorrido planificado");
      setForm((f) => ({ ...f, planned: [], vehicle_plate: "", notes: "" }));
      refresh();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este recorrido planificado?")) return;
    try { await api.delete(`/supervisor-routes/${id}`); toast.success("Recorrido eliminado"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Fix S (2026-09-10): replicar la planificación de una semana al resto de
  // las semanas del mes, sin reemplazar la carga manual por día.
  const [replicateBase, setReplicateBase] = useState("");
  const [replicateTargets, setReplicateTargets] = useState([]);
  const [replicateOverwrite, setReplicateOverwrite] = useState(false);
  const [replicatePreview, setReplicatePreview] = useState(null);
  const [replicateBusy, setReplicateBusy] = useState(false);

  const availableTargets = useMemo(
    () => (replicateBase ? weeksOfMonth(mondayOf(replicateBase)).filter((w) => w !== mondayOf(replicateBase)) : []),
    [replicateBase]
  );
  useEffect(() => { setReplicateTargets(availableTargets); setReplicatePreview(null); }, [replicateBase]); // eslint-disable-line

  const toggleTarget = (w) => setReplicateTargets((ts) => (ts.includes(w) ? ts.filter((x) => x !== w) : [...ts, w]));

  const runReplicate = async (dryRun) => {
    if (!form.supervisor_id) return toast.error("Elegí el supervisor arriba primero");
    if (!replicateBase) return toast.error("Elegí la semana base");
    if (replicateTargets.length === 0) return toast.error("Elegí al menos una semana destino");
    setReplicateBusy(true);
    try {
      const { data } = await api.post("/supervisor-routes/replicate-week", {
        supervisor_id: form.supervisor_id, base_week_start: mondayOf(replicateBase),
        target_week_starts: replicateTargets, overwrite: replicateOverwrite, dry_run: dryRun,
      });
      if (dryRun) {
        setReplicatePreview(data);
      } else {
        toast.success(`Recorridos replicados: ${data.created} nuevo(s), ${data.overwritten} sobrescrito(s), ${data.skipped} sin tocar.`);
        setReplicatePreview(null); setReplicateBase(""); refresh();
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setReplicateBusy(false); }
  };

  const totals = useMemo(() => {
    const planned = routes.reduce((s, r) => s + r.planned_count, 0);
    const executed = routes.reduce((s, r) => s + r.executed_count, 0);
    return { planned, executed, pct: planned ? Math.round((executed / planned) * 1000) / 10 : null };
  }, [routes]);

  return (
    <div style={{ padding: "20px 32px", maxWidth: 1200, margin: "0 auto" }} data-testid="supervisor-routes-page">
      <Link to={backLink} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 12, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}><ArrowLeft size={14}/> Volver</Link>
      {/* Fix A (2026-08-28): renombrado a "Supervisores" por pedido del cliente. */}
      <h1 style={{ fontSize: 26, fontWeight: 900, margin: "4px 0 4px", color: "#0A0A0A", letterSpacing: "-0.02em" }}>Supervisores</h1>
      <p style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 18 }}>Planificá qué objetivos recorre cada supervisor por día y compará contra lo efectivamente marcado (Servicios → check-in/check-out). El kilometraje combina GPS (checkins) y odómetro (combustible cargado en Logística).</p>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 11, color: "var(--invict-text-muted)", marginBottom: 16, padding: "10px 12px", background: "var(--invict-bone-2)", borderRadius: 6 }} data-testid="route-checkins-log-note">
        <Info size={13} style={{ flexShrink: 0, marginTop: 1 }}/> Para ver el detalle de cada marcación (hora de ingreso/egreso, cuánto tiempo estuvo y dónde marcó en el mapa), entrá a <Link to={isAdmin ? "/admin/marcaciones" : "/supervisor/marcaciones"} style={{ color: "var(--invict-red)", fontWeight: 800 }} data-testid="route-checkins-log-link">Marcaciones de Supervisores</Link>.
      </div>
      {!isAdmin && (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 11, color: "var(--invict-text-muted)", marginTop: -8, marginBottom: 16, padding: "10px 12px", background: "var(--invict-bone-2)", borderRadius: 6 }} data-testid="route-checkin-note">
          <Info size={13} style={{ flexShrink: 0, marginTop: 1 }}/> Una parada se cuenta como <b>efectuada</b> cuando hacés check-in en ese objetivo. Usá el botón "Marcar check-in" en cada parada pendiente, o entrá directo a <Link to="/supervisor/checkin" style={{ color: "var(--invict-red)", fontWeight: 800 }}>Check-in en Objetivo</Link>.
        </div>
      )}

      {/* Planificador */}
      <form onSubmit={submit} className="card" style={{ padding: 16, marginBottom: 18 }} data-testid="route-form">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 8 }}>
          {isAdmin && (
            <div className="field"><label>Supervisor *</label>
              <select required value={form.supervisor_id} onChange={(e) => setForm({ ...form, supervisor_id: e.target.value })} data-testid="route-supervisor">
                <option value="">Elegir...</option>
                {supervisors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
          <div className="field"><label>Fecha *</label><input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="route-date"/></div>
          <div className="field"><label>Vehículo (patente, opcional)</label><input value={form.vehicle_plate} onChange={(e) => setForm({ ...form, vehicle_plate: e.target.value.toUpperCase() })} placeholder="AA123BB" data-testid="route-plate"/></div>
        </div>

        <button type="button" onClick={suggestFromSchedule} className="btn-ghost" style={{ marginBottom: 10, fontSize: 11, padding: "7px 12px" }} data-testid="route-suggest-schedule">
          <Sparkles size={12} style={{ marginRight: 6 }}/>Sugerir mis objetivos asignados
        </button>

        <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
          <select value={pickClient} onChange={(e) => { setPickClient(e.target.value); setPickSite(""); }} data-testid="route-add-client-select" style={{ flex: "1 1 200px" }}>
            <option value="">Agregar objetivo al plan...</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <div style={{ flex: "1 1 180px" }}>
            <SiteSelect clientId={pickClient} value={pickSite} onChange={setPickSite} testIdPrefix="route-pick"/>
          </div>
          <button type="button" onClick={addClient} className="btn-ghost" data-testid="route-add-client-btn"><Plus size={14}/></button>
        </div>

        {form.planned.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--invict-text-muted)", padding: "6px 0 12px" }} data-testid="route-planned-empty">Sin objetivos agregados al plan todavía.</div>
        ) : (
          <div style={{ marginBottom: 12 }} data-testid="route-planned-list">
            {form.planned.map((p, i) => (
              <div key={planKey(p)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "var(--invict-bone-2)", borderRadius: 6, marginBottom: 6 }} data-testid={`route-planned-item-${planKey(p)}`}>
                <span style={{ fontSize: 11, fontWeight: 800, color: "var(--invict-text-muted)", width: 18 }}>{i + 1}.</span>
                <span style={{ flex: 1, fontSize: 12, fontWeight: 700 }}>{p.client_name}{p.site_name ? ` · ${p.site_name}` : ""}</span>
                <button type="button" onClick={() => moveClient(i, -1)} disabled={i === 0} style={{ border: "none", background: "transparent", cursor: "pointer", opacity: i === 0 ? 0.3 : 1 }} data-testid={`route-move-up-${planKey(p)}`}><ArrowUp size={13}/></button>
                <button type="button" onClick={() => moveClient(i, 1)} disabled={i === form.planned.length - 1} style={{ border: "none", background: "transparent", cursor: "pointer", opacity: i === form.planned.length - 1 ? 0.3 : 1 }} data-testid={`route-move-down-${planKey(p)}`}><ArrowDown size={13}/></button>
                <button type="button" onClick={() => removeClient(planKey(p))} style={{ border: "none", background: "transparent", color: "#BF3437", cursor: "pointer" }} data-testid={`route-remove-${planKey(p)}`}><X size={14}/></button>
              </div>
            ))}
          </div>
        )}
        <div className="field"><label>Notas</label><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="route-notes"/></div>
        <button type="submit" disabled={busy} className="btn-primary" style={{ width: "100%", padding: 12 }} data-testid="route-submit">{busy ? "Guardando..." : "Planificar recorrido"}</button>
      </form>

      {/* Replicar semana */}
      <div className="card" style={{ padding: 16, marginBottom: 18 }} data-testid="route-replicate-card">
        <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A", marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
          <CalendarDays size={14} style={{ color: "#BF3437" }}/> Repetir una semana en el resto del mes
        </div>
        <p style={{ fontSize: 11, color: "var(--invict-text-muted)", marginBottom: 10 }}>Elegí una semana que ya tenga el recorrido cargado día por día y replicalo a las demás semanas del mismo mes (mismo día de la semana). La carga manual sigue intacta: esto es un atajo, no la reemplaza.</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 10 }}>
          <div className="field"><label>Semana base (elegí cualquier día de esa semana)</label>
            <input type="date" value={replicateBase} onChange={(e) => setReplicateBase(e.target.value)} data-testid="route-replicate-base"/>
          </div>
        </div>
        {replicateBase && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>Replicar a estas semanas del mes:</div>
            {availableTargets.length === 0 && <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>No hay otras semanas en ese mes.</div>}
            {availableTargets.map((w) => (
              <label key={w} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "4px 0", cursor: "pointer" }} data-testid={`route-replicate-target-${w}`}>
                <input type="checkbox" checked={replicateTargets.includes(w)} onChange={() => toggleTarget(w)} data-testid={`route-replicate-target-checkbox-${w}`}/>
                {weekLabel(w)}
              </label>
            ))}
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "8px 0 0", cursor: "pointer" }}>
              <input type="checkbox" checked={replicateOverwrite} onChange={(e) => setReplicateOverwrite(e.target.checked)} data-testid="route-replicate-overwrite"/>
              Sobrescribir fechas que ya tienen un recorrido cargado
            </label>
          </div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={() => runReplicate(true)} disabled={replicateBusy || !replicateBase} className="btn-ghost" style={{ padding: "9px 16px", fontSize: 12 }} data-testid="route-replicate-preview">Vista previa</button>
          {replicatePreview && (
            <button type="button" onClick={() => runReplicate(false)} disabled={replicateBusy} className="btn-primary" style={{ padding: "9px 16px", fontSize: 12 }} data-testid="route-replicate-confirm">Confirmar y replicar</button>
          )}
        </div>
        {replicatePreview && (
          <div style={{ marginTop: 12, padding: "10px 12px", background: "var(--invict-bone-2)", borderRadius: 6, fontSize: 12 }} data-testid="route-replicate-preview-result">
            Se van a crear <b>{replicatePreview.to_create.length}</b> recorrido(s) nuevo(s){replicatePreview.to_overwrite.length > 0 && <> y {replicateOverwrite ? <b>sobrescribir {replicatePreview.to_overwrite.length}</b> : <><b>{replicatePreview.to_overwrite.length}</b> ya tienen planificación cargada (no se van a tocar salvo que marqués "Sobrescribir")</>}</>} — total {replicatePreview.total} fecha(s) evaluada(s).
          </div>
        )}
      </div>

      {/* Filtros / Panel de control */}
      <div className="card" style={{ padding: 14, marginBottom: 18, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }} data-testid="route-filters">
        {isAdmin && (
          <div className="field"><label>Supervisor</label>
            <select value={filters.supervisor_id} onChange={(e) => setFilters({ ...filters, supervisor_id: e.target.value })} data-testid="route-filter-supervisor">
              <option value="">Todos</option>
              {supervisors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}
        <div className="field"><label>Vehículo</label><input value={filters.plate} onChange={(e) => setFilters({ ...filters, plate: e.target.value.toUpperCase() })} placeholder="Todos" data-testid="route-filter-plate"/></div>
        <div className="field"><label>Desde</label><input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="route-filter-from"/></div>
        <div className="field"><label>Hasta</label><input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="route-filter-to"/></div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 20 }} data-testid="route-dashboard-cards">
        <Card icon={RouteIcon} label="Visitas planificadas" value={totals.planned}/>
        <Card icon={CheckCircle2} label="Visitas efectuadas" value={totals.executed} sub={totals.pct != null ? `${totals.pct}% cumplido` : "—"}/>
        <Card icon={Gauge} label="Km por GPS (checkins)"
              value={kmFuel?.gps ? `${kmFuel.gps.distance_km} km` : "—"}
              sub={!kmFuel?.gps ? "Elegí un supervisor para calcularlo" : kmFuel.gps.reason || `${kmFuel.gps.visits_count} check-in(s) en el período`}/>
        <Card icon={Fuel} label="Km/consumo por odómetro"
              value={kmFuel?.odometer_fuel?.distance_km != null ? `${kmFuel.odometer_fuel.distance_km} km · ${kmFuel.odometer_fuel.liters_per_100km ?? "—"} L/100km` : "—"}
              sub={kmFuel?.odometer_fuel?.reason || (kmFuel?.odometer_fuel?.total_amount ? `Gasto: $${kmFuel.odometer_fuel.total_amount.toLocaleString("es-AR")}` : null)}/>
      </div>
      {kmFuel?.note && (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 11, color: "var(--invict-text-muted)", marginTop: -10, marginBottom: 18, padding: "10px 12px", background: "var(--invict-bone-2)", borderRadius: 6 }} data-testid="route-km-note">
          <Info size={13} style={{ flexShrink: 0, marginTop: 1 }}/> {kmFuel.note}
        </div>
      )}

      <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A", marginBottom: 10 }}>Recorridos planificados</div>
      <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="route-list">
        {routes.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "var(--invict-text-muted)", fontSize: 13 }}>Sin recorridos planificados en el período.</div>}
        {routes.map((r) => (
          <div key={r.id} style={{ padding: "14px 16px", borderBottom: "1px solid var(--invict-bone-3)" }} data-testid={`route-row-${r.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div>
                <span style={{ fontSize: 13, fontWeight: 800 }}>{r.supervisor_name}{r.supervisor_deleted && <DeletedUserBadge id={r.id} />}</span>
                <span style={{ fontSize: 11, color: "var(--invict-text-muted)", marginLeft: 8 }}>{r.date}{r.vehicle_plate ? ` · ${r.vehicle_plate}` : ""}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: r.completion_pct === 100 ? "#16a34a" : "#0A0A0A" }} data-testid={`route-pct-${r.id}`}>{r.executed_count}/{r.planned_count} efectuados</span>
                <button onClick={() => del(r.id)} style={{ border: "none", background: "transparent", color: "#BF3437", cursor: "pointer" }} data-testid={`route-delete-${r.id}`}><Trash2 size={15}/></button>
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {r.planned_clients.map((pc) => (
                <span key={planKey(pc)} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, padding: "4px 9px", borderRadius: 10, background: pc.executed ? "#16a34a22" : "var(--invict-bone-2)", color: pc.executed ? "#16a34a" : "var(--invict-text-muted)" }} data-testid={`route-client-status-${r.id}-${planKey(pc)}`}>
                  {pc.executed ? <CheckCircle2 size={12}/> : <Circle size={12}/>} {pc.client_name}{pc.site_name ? ` · ${pc.site_name}` : ""}
                  {!pc.executed && !isAdmin && r.supervisor_id === user.id && (
                    <Link to={`/supervisor/checkin?client_id=${pc.client_id}`} style={{ color: "var(--invict-red)", fontWeight: 900, textDecoration: "underline", marginLeft: 2 }} data-testid={`route-mark-checkin-${r.id}-${planKey(pc)}`}>
                      Marcar check-in
                    </Link>
                  )}
                </span>
              ))}
            </div>
            {r.notes && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", fontStyle: "italic", marginTop: 6 }}>{r.notes}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value, sub }) {
  return (
    <div className="card" style={{ padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, color: "var(--invict-text-muted)" }}>
        <Icon size={14}/><span style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
      </div>
      <div style={{ fontSize: 20, fontWeight: 900, color: "#0A0A0A" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
